import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowLeft, Calendar, Search, Star, Users, Briefcase, BookOpen, Globe, Link2, Timer, Smile } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { CALENDLY_URL } from '@/lib/site';

const audiences = [
  { icon: <Users className="text-primary" size={28} />, title: 'Local Service Businesses', desc: "Contractors, painters, cleaners, and more—get found in your city and grow your leads." },
  { icon: <Briefcase className="text-primary" size={28} />, title: 'B2B Companies', desc: "From SaaS to corporate services, we help you reach decision-makers and build awareness." },
  { icon: <BookOpen className="text-primary" size={28} />, title: 'Education', desc: "Music, dance, and art schools—attract more students and fill your programs." },
  { icon: <Globe className="text-primary" size={28} />, title: 'Other Businesses', desc: "Medical clinics, travel, law firms, and more—we've helped dozens of industries succeed." },
];

const strategies = [
  { icon: <CheckCircle className="text-primary" size={24} />, title: "Technical Foundation", desc: "Fast, user-friendly, error-free sites. We fix 404s, optimize meta data, and ensure your site is built to win on Google." },
  { icon: <BookOpen className="text-primary" size={24} />, title: "Content Strategy", desc: "Intensive keyword research, competitive page writing, and blog strategies that drive leads and fill your funnel." },
  { icon: <Search className="text-primary" size={24} />, title: "Local SEO", desc: "Google Map Pack optimization, business profile audits, and directory consistency for more local leads." },
  { icon: <Link2 className="text-primary" size={24} />, title: "Backlinking", desc: "Quality links from trusted sites. We use outreach, directories, and publisher relationships to boost your authority." },
];

const SeoServices = () => (
  <div className="min-h-screen pt-16 bg-gradient-to-b from-primary/10 to-background dark:from-primary/5">
    <Helmet>
      <title>SEO Services for Small Businesses in the USA | Omnicraft Services</title>
      <meta name="description" content="Omnicraft Services provides SEO services for small businesses in the USA. Get found in your city—Dallas, Houston, and beyond—with local and national SEO strategies." />
      <link rel="icon" type="image/png" href="/logo.png" />
      <link rel="apple-touch-icon" href="/logo.png" />
    </Helmet>
    {/* Hero Section */}
    <section className="py-12 md:py-20">
      <div className="container-custom max-w-4xl mx-auto text-center">
        <Link to="/services/seo-analytics" className="inline-flex items-center text-primary mb-6 hover:underline text-sm md:text-base">
          <ArrowLeft size={16} className="mr-2" /> Back to SEO & Analytics
        </Link>
        <div className="flex flex-col items-center justify-center mb-4">
          <Search size={48} className="text-primary mb-2" />
          <h1 className="text-4xl md:text-5xl font-bold mb-2">Small Business SEO Services</h1>
        </div>
        <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-6">
          Search engine optimization is more challenging than ever. Whether you run a construction business in Dallas, a law firm in Houston, or a company anywhere in the US, <span className="font-semibold text-primary">Omnicraft Services</span> can help you generate leads and sales from Google. <span className="font-semibold">Ready to grow?</span> Send us a note!
        </p>
      </div>
    </section>

    {/* Who We Help */}
    <section className="py-8 md:py-12 bg-slate-50 dark:bg-slate-900/50 border-y border-primary/10">
      <div className="container-custom max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-primary flex items-center gap-2"><Users className="text-primary" size={28} /> Who We Can Help</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {audiences.map((aud, idx) => (
            <Card key={idx} className="flex flex-col items-center p-6 bg-white/80 dark:bg-slate-800/80 border border-primary/20 shadow-sm text-center">
              {aud.icon}
              <h3 className="text-lg font-semibold mt-2 mb-1">{aud.title}</h3>
              <p className="text-sm text-slate-700 dark:text-slate-300">{aud.desc}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* Strategy Section */}
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-primary flex items-center gap-2"><Star className="text-yellow-400" size={28} /> The SEO Strategy</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {strategies.map((s, idx) => (
            <Card key={idx} className="flex flex-col items-center p-6 bg-white/80 dark:bg-slate-800/80 border border-primary/20 shadow-sm text-center">
              {s.icon}
              <h3 className="text-lg font-semibold mt-2 mb-1">{s.title}</h3>
              <p className="text-sm text-slate-700 dark:text-slate-300">{s.desc}</p>
            </Card>
          ))}
        </div>
        <div className="mt-10 max-w-3xl mx-auto">
          <Card className="bg-primary/5 border-primary/20 shadow-md">
            <CardContent className="p-6 md:p-8">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2"><Timer className="text-primary" size={24} /> The SEO Time Frame</h3>
              <p className="mb-4">If you have an established online presence, we may be able to get you quick wins—like fixing 404s, redirect chains, or thin content. You could see more leads in as little as three months. For startups or competitive industries, SEO is a long-term investment. It may take six months or more, but the results are worth it!</p>
              <p className="mb-4">SEO is an investment, not a quick fix. If you need instant results, ask us about Google Ads. But if you want to build lasting growth, we'll help you every step of the way.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>

    {/* Pricing Section */}
    <section className="py-12 md:py-20">
      <div className="container-custom max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-primary">Pricing</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Lump Sum */}
          <Card className="bg-black/90 border border-primary/20 shadow-lg flex flex-col justify-between">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold mb-4 text-white">LUMP SUM</h3>
              <ul className="mb-8 space-y-2 text-white/90">
                <li>Design And Development <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>$17.50/mo Hosting <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>$70 fee Per Page after 5 <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>+$35/mo Unlimited Edits Add-on <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>+$175 To Add A Blog <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li className="text-slate-400 flex items-center">24/7 Support <span className="ml-2">&#10060;</span></li>
                <li className="text-slate-400 flex items-center">Lifetime Updates <span className="ml-2">&#10060;</span></li>
              </ul>
              <div className="mt-auto">
                <div className="text-4xl font-bold text-white mb-2">$2660</div>
                <div className="text-white/70 mb-4">+ $17.50/mo Hosting</div>
                <Button asChild size="lg" className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold">
                  <Link to="/contact">GET STARTED</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          {/* Monthly */}
          <Card className="bg-black/90 border border-primary/20 shadow-lg flex flex-col justify-between ring-2 ring-primary/60">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold mb-4 text-white">MONTHLY</h3>
              <ul className="mb-8 space-y-2 text-white/90">
                <li>Design And Development <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Includes Hosting <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>$70 fee Per Page After 5 <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>+$175 To Add A Blog <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Unlimited Edits <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>24/7 Support <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Lifetime Updates <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
              </ul>
              <div className="mt-auto">
                <div className="text-4xl font-bold text-white mb-2">$122.50</div>
                <div className="text-white/70 mb-4">Per Month</div>
                <Button asChild size="lg" className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold">
                  <Link to="/contact">GET STARTED</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          {/* Ecommerce */}
          <Card className="bg-black/90 border border-primary/20 shadow-lg flex flex-col justify-between">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold mb-4 text-white">ECOMMERCE</h3>
              <ul className="mb-8 space-y-2 text-white/90">
                <li>Custom Shopify Store <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Configure Any And All Apps <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Integrated Shipping <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Shopify Tutorial Walkthrough <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li>Fully Editable In Shopify CMS <CheckCircle className="inline ml-2 text-primary" size={20} /></li>
                <li className="text-slate-400 flex items-center">+$35/mo Unlimited Edits <span className="ml-2">&#10060;</span></li>
                <li className="text-slate-400 flex items-center">24/7 Support <span className="ml-2">&#10060;</span></li>
              </ul>
              <div className="mt-auto">
                <div className="text-4xl font-bold text-white mb-2">$5600</div>
                <div className="text-white/70 mb-4">Starting</div>
                <Button asChild size="lg" className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold">
                  <Link to="/contact">GET STARTED</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>

    {/* Testimonial Section */}
    <section className="py-10 md:py-16 bg-slate-100 dark:bg-slate-800 border-t border-primary/10">
      <div className="container-custom max-w-2xl mx-auto text-center">
        <div className="flex flex-col items-center mb-4">
          <Star className="text-yellow-400 mb-2" size={36} />
          <p className="italic text-lg md:text-xl text-slate-700 dark:text-slate-200 mb-2">"Omnicraft's SEO team helped us double our leads in just a few months. We finally show up on Google!"</p>
          <span className="font-semibold text-primary">— Satisfied Client</span>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-10 md:py-16 bg-primary text-white border-t border-primary/20">
      <div className="container-custom text-center max-w-2xl mx-auto">
        <h2 className="mb-4 md:mb-6 text-3xl font-bold flex items-center justify-center gap-2">
          <Smile className="text-white" size={32} /> Ready to Grow with SEO?
        </h2>
        <p className="text-base md:text-lg mb-6 md:mb-8 opacity-90">
          Let's chat about your business, your goals, and how we can help you win on Google. Reach out for a friendly proposal or just to ask questions!
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
          <Button asChild size="lg" variant="secondary">
            <Link to="/contact">Get My SEO Proposal</Link>
          </Button>
          <Button 
            asChild
            size="lg" 
            variant="outline" 
            className="border-white text-white hover:bg-white hover:text-primary"
          >
            <a 
              href={CALENDLY_URL} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Calendar className="mr-2 h-5 w-5" /> Book a Call
            </a>
          </Button>
        </div>
      </div>
    </section>

    {/* FAQ Section */}
    <section className="py-12 bg-slate-50 dark:bg-slate-900/50 border-t border-primary/10 mt-12">
      <div className="container-custom max-w-3xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-primary">Frequently Asked Questions</h2>
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold mb-2">Do you offer local SEO for cities like Dallas and Houston?</h3>
            <p>Yes! We help small businesses in Dallas, Houston, and across the USA improve their local search rankings and attract more customers.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">How long does it take to see SEO results?</h3>
            <p>SEO is a long-term investment. Some clients see results in as little as three months, but most see significant growth within six months or more.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Can you help with Google My Business optimization?</h3>
            <p>Absolutely. We optimize your Google Business Profile and ensure your business appears in the Google Map Pack for your city.</p>
          </div>
        </div>
      </div>
      {/* FAQ Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Do you offer local SEO for cities like Dallas and Houston?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes! We help small businesses in Dallas, Houston, and across the USA improve their local search rankings and attract more customers."
            }
          },
          {
            "@type": "Question",
            "name": "How long does it take to see SEO results?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "SEO is a long-term investment. Some clients see results in as little as three months, but most see significant growth within six months or more."
            }
          },
          {
            "@type": "Question",
            "name": "Can you help with Google My Business optimization?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. We optimize your Google Business Profile and ensure your business appears in the Google Map Pack for your city."
            }
          }
        ]
      }) }} />
    </section>
  </div>
);

export default SeoServices; 